import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ArrowRight, Zap, Clock, Target, RefreshCcw } from "lucide-react";
import { motion } from "framer-motion";

export default function TodayTraining() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/training/today").then((r) => setData(r.data));
  }, []);

  if (!data) return <p className="p-8">Preparando tu entrenamiento de hoy...</p>;

  const total = (data.review?.length || 0) + (data.adaptive?.length || 0);

  if (total === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/15 text-primary grid place-items-center">
          <Sparkles size={28} />
        </div>
        <h1 className="heading text-3xl font-extrabold mt-4">Todo al día</h1>
        <p className="text-foreground/70 mt-2">No hay ejercicios pendientes hoy. ¡Buen trabajo!</p>
        <Button className="mt-6 rounded-full" onClick={() => nav("/dashboard")}>Volver al panel</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="today-page">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-semibold">Personalizado</p>
        <h1 className="heading text-4xl sm:text-5xl font-extrabold mt-1 tracking-tight">Mi entrenamiento de hoy</h1>
        <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
          Combinación adaptativa preparada según tu rendimiento reciente.
          Duración estimada: <b>{data.estimated_minutes} min</b>.
        </p>
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-primary/30 bg-primary/5 p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground grid place-items-center"><RefreshCcw size={20} /></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Repaso</div>
            <div className="heading text-2xl font-extrabold">{data.review?.length || 0} ítems</div>
            <div className="text-xs text-foreground/70">de fallos recientes</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Target size={20} /></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Sesión activa</div>
            <div className="heading text-2xl font-extrabold">{data.current_session ? `Nº ${data.current_session}` : "—"}</div>
            <div className="text-xs text-foreground/70">{data.adaptive?.length || 0} ejercicios adaptativos</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent text-accent-foreground grid place-items-center"><Clock size={20} /></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Duración</div>
            <div className="heading text-2xl font-extrabold">{data.estimated_minutes} min</div>
            <div className="text-xs text-foreground/70">~2 min por ítem</div>
          </div>
        </div>
      </motion.div>

      {/* Primary CTA: continuous flow */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/10 via-background to-accent/10 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground grid place-items-center shrink-0"><Sparkles size={22} /></div>
          <div>
            <p className="heading text-xl sm:text-2xl font-extrabold leading-tight">Empezar ahora ({total} ítems)</p>
            <p className="text-sm text-foreground/70 mt-1 max-w-md">
              Repaso + entrenamiento adaptativo en un único flujo continuo. Sin volver al panel.
            </p>
          </div>
        </div>
        <Button
          size="lg"
          className="rounded-full h-12 px-6 shrink-0"
          onClick={() => nav("/today/play")}
          data-testid="today-start-continuous"
        >
          Empezar entrenamiento <ArrowRight size={16} className="ml-1" />
        </Button>
      </motion.div>

      {data.focus_domains?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Dominios foco de hoy</p>
          <div className="flex flex-wrap gap-2">
            {data.focus_domains.map((d) => (
              <Badge key={d} className="bg-accent/15 text-accent capitalize">{d.replace(/_/g, " ")}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.review?.length > 0 && (
          <Card className="rounded-2xl border-accent/30 bg-accent/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2 text-accent">
                <Zap size={18} /> <span className="heading font-bold text-lg">Empezar por repaso</span>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                {data.review.length} ejercicios similares a tus últimos fallos.
              </p>
              <Button className="rounded-full h-11" onClick={() => nav("/review")} data-testid="today-start-review">
                Empezar repaso <ArrowRight size={16} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}
        {data.current_session && (
          <Card className="rounded-2xl border-primary/30 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2 text-primary">
                <Sparkles size={18} /> <span className="heading font-bold text-lg">Continuar Sesión {data.current_session}</span>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                {data.adaptive?.length || 0} ejercicios seleccionados adaptativamente.
              </p>
              <Button className="rounded-full h-11" onClick={() => nav(`/sessions/${data.current_session}`)} data-testid="today-start-session">
                Ir a la sesión <ArrowRight size={16} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
